import { Link, useParams } from "react-router-dom";

import { HttpError } from "../api/rest";
import type { Trade, WalletMetrics } from "../api/types";
import { Empty, Panel } from "../components/Panel";
import { PolymarketProfile, Tier } from "../components/Tier";
import { useWallet, useWalletTrades } from "../hooks/useWallet";
import { formatTime, shortId } from "../lib/format";
import { aggressionPercent, horizonLabel, horizonLagMs } from "../lib/tier";
import { ourFill, ourNotional, ourOutcome, ourSide, tradeKey } from "../lib/trades";

/**
 * Профиль кошелька-контрагента (веха M6).
 *
 * Отвечает на два разных вопроса и держит их раздельно: «какой это участник»
 * (метрики по горизонтам, посчитанные по публичному Data API) и «где мы с ним
 * встречались» (наши сделки из собственного журнала). Третий вопрос —
 * «насколько этому можно верить» — живёт в панели выгрузки: тир, посчитанный по
 * обрывку истории, выглядит так же уверенно, как посчитанный по всей.
 */
export function WalletPage() {
  const { address } = useParams();
  const wallet = useWallet(address);
  const trades = useWalletTrades(address);

  if (!address) {
    return (
      <Panel title="Кошелёк не выбран">
        <Empty>
          В адресе нет кошелька. <Link to="/">Вернуться к списку рынков</Link>.
        </Empty>
      </Panel>
    );
  }

  if (wallet.isLoading && !wallet.data) {
    return (
      <Panel title="Кошелёк" subtitle={shortId(address)}>
        <Empty>загрузка…</Empty>
      </Panel>
    );
  }

  if (wallet.error && !wallet.data) {
    // 404 здесь — ОСМЫСЛЕННЫЙ ответ, а не поломка: контрагентов система узнаёт
    // только из наших сделок, поэтому «кошелёк не встречался» означает «мы с
    // ним не торговали». Показать это той же красной ошибкой, что и падение
    // сервиса, значило бы отправить оператора чинить исправную систему.
    const unknown = wallet.error instanceof HttpError && wallet.error.status === 404;
    const met = (trades.data?.trades.length ?? 0) > 0;
    return (
      <div className="flex flex-col gap-2">
        <Panel title="Кошелёк" subtitle={shortId(address)}>
          {unknown ? (
            // Профиля нет — но это НЕ значит, что кошелька не было. Строка
            // wallets появляется только при включённом профилировании
            // (PROFILE_ENABLED), а ссылку на кошелёк лента рисует у каждой
            // нашей сделки. Написать «мы не торговали» под списком сделок,
            // в которых он стоит, значило бы спорить с собственным журналом.
            <Empty>
              {met
                ? "Профиль этого кошелька не считался: профилирование выключено либо адрес " +
                  "не успел попасть в очередь. Встречи в журнале при этом есть — они ниже."
                : "С этим кошельком мы ещё не торговали. Контрагенты появляются только из " +
                  "наших сделок: публичная лента рынка адресов не несёт вовсе."}
            </Empty>
          ) : (
            <p className="text-sm text-[var(--color-sell)]">{(wallet.error as Error).message}</p>
          )}
          {/* Профиля у нас нет — и ровно поэтому ссылка на биржу здесь нужнее
              всего: адрес известен, а всё, что о нём вообще можно узнать,
              лежит там. */}
          <p className="mt-2 flex items-baseline gap-3 text-xs text-[var(--color-muted)]">
            <PolymarketProfile address={address} />
            <Link to="/" className="underline">
              вернуться к списку рынков
            </Link>
          </p>
        </Panel>

        {met && (
          <Panel
            title="Наши сделки, где он встретился"
            subtitle={trades.data?.truncated ? "показаны не все" : undefined}
          >
            <TradesTable
              trades={trades.data?.trades ?? []}
              loading={trades.isLoading}
              address={address}
            />
          </Panel>
        )}
      </div>
    );
  }

  const data = wallet.data;
  if (!data) return null;

  return (
    <div className="flex flex-col gap-2">
      <Panel
        title="Кошелёк"
        subtitle={data.hot ? "горячая копия" : "из журнала"}
        actions={<Tier tier={data.profile.tier} />}
      >
        <dl className="grid grid-cols-2 gap-x-6 gap-y-1 text-xs sm:grid-cols-4">
          <Field label="адрес">
            {/* Рядом с адресом, а не в углу панели: следующее движение после
                чтения адреса — «посмотреть, чем он ещё торгует», и до сих пор
                адрес для этого копировали руками. */}
            <span className="num break-all">{data.profile.address}</span>{" "}
            <PolymarketProfile address={data.profile.address} />
          </Field>
          {data.profile.label && <Field label="метка">{data.profile.label}</Field>}
          <Field label="первая встреча">{formatTime(data.profile.first_seen_at)}</Field>
          <Field label="последняя встреча">{formatTime(data.profile.last_seen_at)}</Field>
        </dl>
      </Panel>

      <Panel title="Метрики" subtitle="суммы в долларах">
        <MetricsTable rows={data.metrics} backfilled={data.backfill.done} />
        <p className="mt-3 text-xs text-[var(--color-muted)]">
          Тир считается по суточному горизонту. Доля агрессии — сколько сделок кошелёк был
          тейкером; по нашим сделкам она не выводится вовсе (контрагент в них всегда мейкер),
          поэтому источник здесь — публичный Data API.
        </p>
      </Panel>

      <Backfill wallet={data} />

      <Panel
        title="Наши сделки, где он встретился"
        subtitle={trades.data?.truncated ? "показаны не все" : undefined}
        actions={
          // Здесь список короткий и без разбивки по встречным ордерам; полный
          // разбор — «сколько именно он налил и кем были мы» — живёт в истории.
          <Link
            to={`/trades?wallet=${encodeURIComponent(address)}`}
            className="text-xs text-[var(--color-muted)] hover:text-white"
          >
            разобрать в истории →
          </Link>
        }
      >
        <TradesTable
          trades={trades.data?.trades ?? []}
          loading={trades.isLoading}
          address={address}
        />
      </Panel>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <dt className="text-[var(--color-muted)]">{label}</dt>
      <dd>{children}</dd>
    </div>
  );
}

/**
 * Метрики по горизонтам.
 *
 * backfilled решает судьбу ОДНОЙ ячейки — доли агрессии за всё время. Пока
 * история не дочитана, числитель и знаменатель прочитаны с одинаковой глубины и
 * описывают разные множества сделок: у кошелька, агрессивного на треть, первые
 * страницы дают ровно 100 %. Показать это число значило бы уверенно соврать,
 * поэтому до конца обхода в ячейке стоит прочерк — счётчики рядом при этом
 * честно растут.
 */
function MetricsTable({ rows, backfilled }: { rows: WalletMetrics[]; backfilled: boolean }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs">
        <thead>
          <tr className="text-left text-[var(--color-muted)]">
            <th className="pb-2 font-normal">Горизонт</th>
            <th className="pb-2 text-right font-normal">Сделок</th>
            <th className="pb-2 text-right font-normal">Тейкером</th>
            <th className="pb-2 text-right font-normal">Оборот</th>
            <th className="pb-2 text-right font-normal">Средняя</th>
            <th className="pb-2 text-right font-normal">Агрессия</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((m) => (
            <tr key={m.horizon} className="border-t border-[var(--color-border)]">
              <td className="py-1 pr-3">
                {horizonLabel(m.horizon)}
                {/* Горизонт «всё время» перестаёт обновляться, как только выгрузка
                    дочитала историю, и дальше это СНИМОК того момента. Замер
                    2026-08-12: 3114 сделок в строке против 3714 у индексатора —
                    расхождение с биржей на треть, ничем на экране не объяснённое.
                    Строка со временем снимка и есть объяснение. */}
                {horizonLagMs(m, rows) > 0 && (
                  <span
                    className="ml-1 text-[10px] text-[var(--color-muted)]"
                    title={
                      "снимок на момент завершения выгрузки: этот горизонт больше " +
                      "не пересчитывается, и сделки после него сюда не попадают"
                    }
                  >
                    снимок {formatTime(m.updated_at)}
                  </span>
                )}
              </td>
              <td className="num py-1 pr-3 text-right">{m.trade_count}</td>
              <td className="num py-1 pr-3 text-right text-[var(--color-muted)]">
                {m.taker_count}
              </td>
              <td className="num py-1 pr-3 text-right">${m.volume}</td>
              <td className="num py-1 pr-3 text-right">${m.avg_size}</td>
              <td className="num py-1 text-right">
                {m.horizon === "all" && !backfilled ? (
                  <span className="text-[var(--color-muted)]" title="история прочитана не вся">
                    —
                  </span>
                ) : (
                  aggressionPercent(m.aggression_ratio)
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Состояние выгрузки истории.
 *
 * Панель существует ровно затем, чтобы незавершённая или упавшая выгрузка была
 * ВИДНА. Без неё тир, посчитанный по первым четырёмстам сделкам из десяти
 * тысяч, выглядит на экране точно так же, как посчитанный по всем.
 */
function Backfill({ wallet }: { wallet: { backfill: { done: boolean; offset: number; attempts: number; last_error?: string } } }) {
  const { done, offset, attempts, last_error: lastError } = wallet.backfill;
  return (
    <Panel title="Выгрузка истории" subtitle={done ? "завершена" : "продолжается"}>
      <dl className="grid grid-cols-2 gap-x-6 gap-y-1 text-xs sm:grid-cols-4">
        <Field label="прочитано записей">
          <span className="num">{offset}</span>
        </Field>
        <Field label="проходов">
          <span className="num">{attempts}</span>
        </Field>
      </dl>
      {lastError ? (
        <p className="mt-2 text-xs text-[var(--color-warn)]">
          последняя ошибка: {lastError}
        </p>
      ) : null}
      {!done && !lastError ? (
        <p className="mt-2 text-xs text-[var(--color-muted)]">
          Горизонт «всё время» читается порциями, чтобы один глубокий кошелёк не занял очередь
          целиком. Метрики за час и сутки от этого не зависят и уже точны.
        </p>
      ) : null}
    </Panel>
  );
}

/**
 * Кем кошелёк был в этой встрече.
 *
 * ТЕЙКЕРСКАЯ сделка — единственная, где он наш контрагент: мы сняли его ордер.
 * В мейкерской он сосед по сносу: тот же чужой тейкер-ордер закрыл и нас, и
 * его, а наш настоящий контрагент — агрессор — на проводе отсутствует вовсе, от
 * него есть только taker_order_id. Оператор решает по этой ячейке
 * противоположные вещи: от контрагента, который нас переиграл, уходят, а с
 * соседом по сносу мы стояли по одну сторону.
 */
function encounter(t: Trade, address: string) {
  if (t.trader_side === "TAKER") {
    return <span title="мы сняли его ордер">контрагент</span>;
  }
  if (t.trader_side === "MAKER") {
    // Он и есть агрессор: снял НАШУ заявку. Адрес восстановлен из квитанции
    // транзакции (ADR-0008), на проводе его не было. Это противоположность
    // «рядом» — и путать их нельзя: от того, кто нас бьёт, уходят.
    if (t.taker_address && t.taker_address.toLowerCase() === address.toLowerCase()) {
      return (
        <span
          className="text-[var(--color-sell)]"
          title="он снял нашу заявку: мы стояли в стакане, он пришёл и забрал. Адрес восстановлен из квитанции транзакции"
        >
          агрессор
        </span>
      );
    }
    return (
      <span
        className="text-[var(--color-muted)]"
        title="стоял в стакане рядом с нами: тот же тейкер-ордер закрыл и его; против нас стоял агрессор"
      >
        рядом
      </span>
    );
  }
  // Старые записи без trader_side: гадать нельзя — ответ противоположный.
  return <span className="text-[var(--color-muted)]">—</span>;
}

function TradesTable({
  trades,
  loading,
  address,
}: {
  trades: Trade[];
  loading: boolean;
  /** Чей это профиль: по нему решается, кем кошелёк был во встрече. */
  address: string;
}) {
  if (loading && trades.length === 0) return <Empty>загрузка…</Empty>;
  if (trades.length === 0) {
    return <Empty>Встреч в журнале нет — возможно, сделка ещё не записана.</Empty>;
  }
  return (
    <div className="max-h-[50vh] overflow-y-auto">
      <table className="w-full text-xs">
        <thead className="sticky top-0 bg-[var(--color-panel)]">
          <tr className="text-left text-[var(--color-muted)]">
            <th className="pb-2 font-normal">Время</th>
            <th className="pb-2 font-normal">Рынок</th>
            {/* Кем этот кошелёк был во встрече. Колонка обязательна: список
                отвечает на вопрос «где мы его видели», а не «против кого
                торговали», и без неё сделка, в которой он стоял с нами по одну
                сторону, выглядит как сделка против него (ADR-0007 §1). */}
            <th className="pb-2 font-normal">Встреча</th>
            <th className="pb-2 font-normal">Сторона</th>
            {/* «Наша» в подписи не украшение: в мейкерской сделке числа самой
                записи принадлежат ордеру агрессора, и колонка без имени
                владельца читалась бы как «столько прошло по рынку». */}
            <th className="pb-2 text-right font-normal">Наша цена</th>
            <th className="pb-2 text-right font-normal">Наш размер</th>
            <th className="pb-2 text-right font-normal">Сумма, $</th>
          </tr>
        </thead>
        <tbody>
          {trades.map((t) => {
            // Наш филл, а не сделка целиком: у мейкерской записи price и size
            // описывают ордер агрессора и зачастую ДРУГОЙ исход — живой пример
            // 2026-08-10: наши 5.95 @ 0.84 (Up) приехали строкой «81 @ 0.16».
            // Здесь колонка называется «наши сделки», и показывать в ней чужой
            // размер значило бы отчитаться о сделке, которой оператор не делал.
            // Так же считает страница истории (lib/trades).
            const fill = ourFill(t);
            const side = ourSide(t);
            const outcome = ourOutcome(t);
            const color = side === "BUY" ? "var(--color-buy)" : "var(--color-sell)";
            return (
              <tr key={tradeKey(t)} className="border-t border-[var(--color-border)]">
                <td className="num py-1 pr-3 text-[var(--color-muted)]">
                  {formatTime(t.timestamp)}
                </td>
                <td className="num py-1 pr-3">
                  <Link
                    to={`/market/${encodeURIComponent(t.condition_id)}`}
                    className="hover:text-[var(--color-accent)] hover:underline"
                    title={t.condition_id}
                  >
                    {shortId(t.condition_id)}
                  </Link>
                </td>
                <td className="py-1 pr-3">{encounter(t, address)}</td>
                {/* Сторона НАША, а не контрагента: журнал ведётся от нашего лица,
                    и подменять её на противоположную «чтобы было про него»
                    значило бы завести на одном экране две системы отсчёта.
                    Рядом — наш исход: «покупка» в бинарном рынке без имени
                    токена не значит ничего. */}
                <td className="py-1 pr-3" style={{ color }}>
                  {side === "BUY" ? "покупка" : "продажа"}
                  {outcome && <span className="ml-1 text-[var(--color-muted)]">{outcome}</span>}
                </td>
                <td className="num py-1 pr-3 text-right" style={{ color }}>
                  {fill?.price ?? "—"}
                </td>
                <td className="num py-1 pr-3 text-right">{fill?.size ?? "—"}</td>
                <td className="num py-1 text-right text-[var(--color-muted)]">
                  {ourNotional(t) ?? "—"}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
